/**
 * answerReveal — landing the finished answer, block by block.
 *
 * The engine does not stream tokens. The synthesized answer arrives on
 * `research.completed` as ONE complete markdown blob, so the transcript went
 * from a narrated run ("gathering… retrieving… synthesizing…") to the whole
 * answer appearing in a single frame. The interim lines animate; the thing
 * the user actually waited for did not.
 *
 * The fix reuses the mechanism already in the surface rather than inventing a
 * second motion language: `.reveal-item` (index.css) is the platform's ONE
 * staggered entrance — a fade-and-rise on the brand `draw` curve. What that
 * class cannot do is SEQUENCE, because a CSS `animation-delay` stagger is
 * capped per item and every block is in the DOM from the first frame anyway.
 *
 * So the reveal is driven here, over the rendered output:
 *
 *   • The renderer runs FIRST, in full. Nothing re-parses partial markdown,
 *     so a heading, a `**bold**` run, a link or a `[3]` citation marker can
 *     never be caught half-written — the reveal only decides WHEN a finished
 *     block becomes visible, never what it says.
 *   • That also means it works for every renderer — prose, bullets,
 *     chronology, memo, the markdown fallback — without each of them growing
 *     its own streaming path.
 *
 * Everything below is deliberately DOM-level and pure so it can be reasoned
 * about (and tested) without React.
 */

/**
 * Elements that count as a revealable block.
 *
 * Leaf-ish text carriers, in document order. Containers are excluded by
 * `revealBlocks` so a paragraph is not revealed twice — once as itself and
 * once inside the `<div>` wrapping it.
 */
const BLOCK_SELECTOR =
  'p,li,h1,h2,h3,h4,h5,h6,blockquote,pre,tr,dd,figure,[data-reveal-block]';

/** Never animate an unbounded list — a very long memo would crawl. */
export const MAX_BLOCKS = 48;

/**
 * Marks the subtree whose entrance this module is driving.
 *
 * A stylesheet rule (index.css) switches off `.reveal-item` and
 * `.animate-fade-in` underneath it while the reveal runs. Per-block
 * `animation: none` handles the rows; this handles the CONTAINER, which every
 * renderer wraps its output in — a half-second fade of the whole answer, and
 * the reason a sequenced reveal still read as one paste.
 */
export const REVEALING_ATTR = 'data-answer-revealing';

/**
 * Two frames of head start before the first block moves.
 *
 * `hideBlock` and the first `showBlock` would otherwise be able to land in
 * the same paint: a `setTimeout(…, 0)` is not guaranteed to run after the
 * browser has drawn the hidden state, and a transition between two values the
 * compositor never saw separately is not a transition — the first block would
 * simply appear while the rest faded. Cheap insurance, invisible to read.
 */
export const REVEAL_LEAD_MS = 32;

/** A section that has declared itself one block: `data-reveal-block`. */
const isDeclaredBlock = (node: HTMLElement): boolean =>
  node.hasAttribute('data-reveal-block');

/**
 * The blocks to reveal, in document order.
 *
 * Two rules, and the second one is why the Sources list used to paste in
 * while the prose above it was still arriving:
 *
 *   • A candidate that CONTAINS another candidate is dropped — revealing both
 *     a list and each of its items double-animates the items and stutters.
 *   • UNLESS it declared itself a block. `[data-reveal-block]` means "I am one
 *     thing", and it is taken whole: its own descendants are not collected.
 *     The Sources section is a `<section>` full of `<li>`s that each contain
 *     `<p>`s, so the old rule dropped every row (each contains a paragraph)
 *     and kept the fragments — the title, the quote and the section list were
 *     never hidden at all, which is what the reader saw arrive instantly.
 *
 * Empty blocks are dropped either way — fading in whitespace is just a pause.
 */
export function revealBlocks(root: ParentNode): HTMLElement[] {
  const candidates = Array.from(root.querySelectorAll<HTMLElement>(BLOCK_SELECTOR));
  const declared = candidates.filter(isDeclaredBlock);

  const blocks = candidates
    // Everything inside a declared block belongs to it, not to the sequence.
    .filter((node) => !declared.some((box) => box !== node && box.contains(node)))
    .filter(
      (node) =>
        isDeclaredBlock(node) ||
        !candidates.some((other) => other !== node && node.contains(other))
    )
    .filter((node) => (node.textContent || '').trim() !== '');

  const kept = blocks.slice(0, MAX_BLOCKS);
  // A declared block is never the one dropped by the cap: the Sources list
  // trailing a forty-paragraph memo must still be hidden, or it is back to
  // pasting in over an answer that is still arriving.
  const overflow = blocks.slice(MAX_BLOCKS).filter(isDeclaredBlock);
  return overflow.length ? [...kept, ...overflow] : kept;
}

export interface RevealTiming {
  /** Milliseconds between one block appearing and the next. */
  step: number;
  /** How long a single block's fade lasts. */
  duration: number;
}

/**
 * How fast to run, for a given number of blocks.
 *
 * The STAGGER is budgeted, not the per-block step: a three-paragraph answer
 * and a thirty-block memo both start their last block by `budget`, so nobody
 * waits on the animation to read an answer they already have. Add `duration`
 * for the wall-clock total — roughly 1.1s for a short answer, 1.4s for a long
 * one.
 *
 * The floor and ceiling are what make it READ as a reveal. The ceiling stops
 * a two-paragraph answer sitting on a half-second pause between blocks; the
 * floor stops a forty-block memo flickering past at 20ms a row, which is not
 * a sequence, it is a flash with extra steps.
 */
export function revealTiming(count: number, budget = 1000): RevealTiming {
  if (count <= 0) return { step: 0, duration: 0 };
  const step = Math.max(40, Math.min(280, Math.round(budget / count)));
  return { step, duration: 440 };
}

/** The OS setting wins: no reveal at all when motion is reduced. */
export function prefersReducedMotion(view: Window | null = typeof window === 'undefined' ? null : window): boolean {
  try {
    return !!view?.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  } catch {
    return false;
  }
}

/**
 * Park a block out of sight, before the first frame paints it.
 *
 * `animation: none` is the important line, and it is why this reveal was
 * invisible in the browser while passing every test in jsdom. The renderers
 * put `.reveal-item` on their own rows, and a CSS ANIMATION outranks an
 * inline style in the cascade — so `opacity: 0` set here was ignored for as
 * long as that animation ran, and what the reader saw was the class's own
 * 340ms fade with every block on top of the next. Dropping the animation
 * inline (an inline `animation` property does beat the class's) hands the
 * block's opacity back to this module.
 */
export function hideBlock(node: HTMLElement): void {
  node.style.animation = 'none';
  node.style.opacity = '0';
  node.style.transform = 'translateY(10px)';
  node.style.willChange = 'opacity, transform';
}

/** Bring one block in — the same fade-and-rise as `.reveal-item`. */
export function showBlock(node: HTMLElement, duration: number): void {
  node.style.transition =
    `opacity ${duration}ms cubic-bezier(0.45, 0, 0.3, 1), ` +
    `transform ${duration}ms cubic-bezier(0.45, 0, 0.3, 1)`;
  node.style.opacity = '1';
  node.style.transform = 'translateY(0)';
}

/**
 * Hand the block back to the stylesheet.
 *
 * Every property this module set is cleared, so a finished (or skipped)
 * reveal leaves no inline styles behind for a later re-render to inherit.
 */
export function clearBlock(node: HTMLElement): void {
  node.style.removeProperty('animation');
  node.style.removeProperty('opacity');
  node.style.removeProperty('transform');
  node.style.removeProperty('transition');
  node.style.removeProperty('will-change');
}
